import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IoService } from 'src/app/services/io/io.service';
import { AuthorizationService } from 'src/app/services/authorization/authorization.service';
import { LocationService } from 'src/app/services/location/location.service';

@Component({
  selector: 'app-note',
  templateUrl: './note.component.html',
  styleUrls: ['./note.component.scss']
})

export class NoteComponent implements OnInit, OnChanges {
  @Input('type') type: string = '';
  @Input('entity') entity: any = {};
  // Notes resolved upstream (e.g. inlined by /view/getView) — when provided,
  // the component skips its own /note/loadNotes round-trip. null = not preloaded.
  @Input('preloadedNotes') preloadedNotes: any[] | null = null;

  note: string = "";
  notes: any = [];
  savingNote: boolean = false;
  canViewNotes: boolean = false;
  canAddNotes: boolean = false;
  canDeleteNotes: boolean = false;

  constructor(
    private io: IoService,
    private authService: AuthorizationService,
    private locationService: LocationService
  ) { }

  ngOnInit(): void {
    if(this.authService.checkPermission('note.' + this.type + '.view')){
      this.canViewNotes = true;
      this.syncNotes();
    }

    if(this.authService.checkPermission('note.' + this.type + '.add')){
      this.canAddNotes = true;
    }

    if(this.authService.checkPermission('note.' + this.type + '.delete')){
      this.canDeleteNotes = true;
    }
  }

  // The host may keep this instance across record navigation (detail-view swaps
  // its document in place) — re-sync when the record or preload changes.
  ngOnChanges(changes: SimpleChanges): void {
    const relevant = changes['preloadedNotes'] || changes['entity'];
    if (relevant && !relevant.firstChange && this.canViewNotes) {
      this.syncNotes();
    }
  }

  private syncNotes() {
    if (this.preloadedNotes != null) {
      this.notes = this.preloadedNotes.map((n: any) => ({ ...n, showActions: false }));
    } else {
      this.loadNotes();
    }
  }

  async addNote(){
    const value = (this.note || '').trim();
    if (!value || this.savingNote) {
      return;
    }
    this.savingNote = true;

    let newNote: any = {
      value,
      context: {},
      referenceId: this.entity._id,
      type: this.type
    }

    if(this.type == 'transaction'){
      newNote.context.status = this.entity.status;
    }

    try {
      // The server builds the note (identity from the session token) and returns
      // it complete — append locally instead of reloading the whole list.
      let addedNote: any = await this.io.post("/note/addNote", {
        note: newNote,
        type: this.type,
        locationId: this.locationService.getActiveLocationId()
      });

      this.notes.push({ ...addedNote, showActions: false });
      this.note = "";
    } finally {
      this.savingNote = false;
    }
  }

  async loadNotes () {
    try {
      let notes: any = await this.io.post("/note/loadNotes", {
        referenceId: this.entity._id,
        type: this.type,
        locationId: this.locationService.getActiveLocationId()
      });

      this.notes = notes;
      this.notes.map((n: any) => {
        n.showActions = false;
      })
    } catch (err: any) {
      if (err?.status === 403) {
        this.canViewNotes = false;
      } else {
        throw err;
      }
    }
  }

  toggleActions(note: any): void {
    note.showActions = !note.showActions;
  }

  async deleteNote(note: any){
    await this.io.post("/note/deleteNote", {
      noteId: note._id,
      type: this.type,
      locationId: this.locationService.getActiveLocationId()
    })

    this.notes = this.notes.filter((n: any) => n._id !== note._id);
  }
}
